import { Box, Flex } from '@chakra-ui/layout';
import {
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Input,
  useToast,
} from '@chakra-ui/react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Typography } from 'packages/common-frontend/components/Typography';
import { GET_CREDIT_FACILITIES_BY_ORGANIZATION } from '~/contexts/CreditFacilitiesProvider/requests/getCreditFacilities.gql';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { WizardComponent } from '~/core/routes/settings/implementations/create/ImplementationWizard';
import { useCreateSingleFacilityImplementationMutation } from '~/core/routes/settings/implementations/implementations.gql.generated';
import { errorToastConfig } from '~/packages/common-frontend/constants';

export const NameImplementation: WizardComponent = ({
  implementationName,
  setImplementationName,
  previousStep,
  selectedOrgs,
}) => {
  const toast = useToast();
  const { user } = useUserAuthOrThrow();
  const featureFlags = useFeatureFlags();
  const navigate = useNavigate();

  const [
    createSingleFacilityImplementation,
    { loading },
  ] = useCreateSingleFacilityImplementationMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        // This query powers CreditFacilitiesContext/CreditFacilitiesProvider.
        query: GET_CREDIT_FACILITIES_BY_ORGANIZATION,
        variables: {
          organizationId: user.organizationId,
          creditFacilitySlugsToHide: featureFlags.creditFacilitySlugsToHide,
        },
      },
    ],
  });

  const onNext = async () => {
    const parties = selectedOrgs
      .filter((org) => org.type !== undefined && org.type !== null)
      .map((org) => ({
        organizationId: org.id,
        partyType: org.type!,
      }));

    try {
      const response = await createSingleFacilityImplementation({
        variables: {
          ownerOrganizationId: user.organizationId,
          creditFacilityName: implementationName,
          creditFacilityParties: parties,
        },
      });

      navigate(
        `/settings/implementations/${response.data?.createSingleFacilityImplementation.id}`,
      );
    } catch (err) {
      toast({
        ...errorToastConfig,
        title: 'There was an error creating a new implementation',
      });
    }
  };

  return (
    <Box width="100%">
      <Box>
        <Typography variant="page-title">Name the Facility</Typography>
        <Typography variant="base-muted">
          This name will be used across the platform and can be modified at any
          time.
        </Typography>
      </Box>
      <Card variant="outline" size="lg" my="2rem">
        <CardBody>
          <FormControl>
            <FormLabel fontWeight="bold">Facility Name</FormLabel>
            <Input
              value={implementationName}
              placeholder="Name"
              onChange={(event) => setImplementationName(event.target.value)}
              autoFocus
            />
          </FormControl>
        </CardBody>
      </Card>
      <Flex justify="space-between">
        <Button onClick={previousStep}>Back</Button>
        <Button
          variant="primary"
          colorScheme="v2-aurora"
          isDisabled={loading || implementationName.length === 0}
          onClick={onNext}
        >
          Create
        </Button>
      </Flex>
    </Box>
  );
};
